import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { batchAPI, auditorAPI } from '../services/api';
import BatchFolderView from '../components/BatchFolderView';
import DashboardLayout from '../components/DashboardLayout';
import { motion } from 'framer-motion';
import { Package, CheckCircle, XCircle, Download, Eye, Upload, Search } from 'lucide-react';

const BatchDetails = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  console.log('[BatchDetails] Component initializing with batchId:', batchId);
  console.log('[BatchDetails] Current user:', user);

  const [batch, setBatch] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImages, setSelectedImages] = useState(new Set());
  const [downloading, setDownloading] = useState(false);
  const [downloadingSingle, setDownloadingSingle] = useState(false);
  const [viewingImage, setViewingImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentViewMode, setCurrentViewMode] = useState('original');

  // Auditor review state (only used when user is auditor/admin)
  const [imageReasons, setImageReasons] = useState({}); // { imageId: reason }
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  useEffect(() => {
    console.log('[BatchDetails] Component mounted');
    return () => {
      console.log('[BatchDetails] Component unmounting');
    };
  }, []);

  useEffect(() => {
    console.log('[BatchDetails] Setting up auto-refresh for batchId:', batchId);
    fetchBatchDetails();
    const interval = setInterval(() => {
      console.log('[BatchDetails] Auto-refresh triggered');
      fetchBatchDetails();
    }, 5000);
    return () => {
      console.log('[BatchDetails] Clearing auto-refresh interval');
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId]);

  useEffect(() => {
    return () => {
      if (imagePreviewUrl) {
        console.log('[BatchDetails] Revoking blob URL on cleanup:', imagePreviewUrl);
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  const fetchBatchDetails = async () => {
    console.log('[fetchBatchDetails] Starting fetch for batchId:', batchId);
    try {
      console.log('[fetchBatchDetails] Calling API endpoints...');
      const [batchRes, imagesRes] = await Promise.all([
        batchAPI.getBatch(batchId),
        batchAPI.getImages(batchId)
      ]);

      console.log('[fetchBatchDetails] Batch data received:', batchRes.data);
      console.log('[fetchBatchDetails] Images data received, count:', imagesRes.data.length);

      const imagesWithMarked = imagesRes.data.filter(img => img.s3_marked_url);
      console.log('[fetchBatchDetails] Images with marked images:', imagesWithMarked.length, '/', imagesRes.data.length);

      setBatch(batchRes.data);
      setImages(imagesRes.data);
      setLoading(false);
    } catch (error) {
      console.error('[fetchBatchDetails] Error fetching batch details:', error);
      alert('Failed to load batch details: ' + (error.response?.data?.detail || error.message));
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedImages.size === images.length) {
      setSelectedImages(new Set());
    } else {
      setSelectedImages(new Set(images.map(img => img.image_id)));
    }
  };

  const handleSelectImage = (imageId) => {
    const newSelected = new Set(selectedImages);
    if (newSelected.has(imageId)) {
      newSelected.delete(imageId);
    } else {
      newSelected.add(imageId);
    }
    setSelectedImages(newSelected);
  };

  const handleDownloadSelected = async () => {
    if (selectedImages.size === 0) {
      alert('Please select at least one image to download');
      return;
    }

    setDownloading(true);
    try {
      const selectedImageIds = Array.from(selectedImages);
      await batchAPI.downloadImages(batchId, selectedImageIds);
      alert(`Successfully downloaded ${selectedImageIds.length} image(s)`);
    } catch (error) {
      console.error('[handleDownloadSelected] Error downloading images:', error);
      alert('Failed to download images');
    } finally {
      setDownloading(false);
    }
  };

  const parseFailedChecks = (failedChecks) => {
    if (!failedChecks) return [];
    if (Array.isArray(failedChecks)) {
      if (failedChecks.every(item => typeof item === 'string' && item.length === 1)) {
        return [failedChecks.join('')];
      }
      return failedChecks.filter(check => check);
    }
    if (typeof failedChecks === 'string') {
      if (failedChecks === '') return [];
      if (failedChecks.includes('||')) {
        return failedChecks.split('||').map(check => check.trim()).filter(check => check);
      }
      const checks = failedChecks.split(',').map(check => check.trim()).filter(check => check);
      if (checks.length === 0 && failedChecks.trim()) {
        return [failedChecks.trim()];
      }
      return checks;
    }
    return [];
  };

  const filteredImages = statusFilter === 'all'
    ? images
    : images.filter(image => {
        if (statusFilter === 'pass') {
          return image.overall_status?.toUpperCase() === 'PASS';
        } else if (statusFilter === 'fail') {
          return image.overall_status?.toUpperCase() === 'FAIL';
        }
        return true;
      });

  const handleViewImage = async (image, viewMode = 'original') => {
    setViewingImage(image);
    setCurrentViewMode(viewMode);
    setLoadingPreview(true);
    setImagePreviewUrl(null);

    try {
      const token = sessionStorage.getItem('access_token');
      const imageUrl = viewMode === 'marked'
        ? `/api/batches/${batchId}/view-marked/${image.image_id}`
        : `/api/batches/${batchId}/view-thumbnail/${image.image_id}`;

      const response = await fetch(imageUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error(`Failed to load image - Status: ${response.status}`);
      }

      const blob = await response.blob();
      const isTiff = blob.type === 'image/tiff' || blob.type === 'image/tif';

      if (isTiff) {
        const UTIF = await import('utif');
        const arrayBuffer = await blob.arrayBuffer();
        const ifds = UTIF.default.decode(arrayBuffer);
        UTIF.default.decodeImage(arrayBuffer, ifds[0]);
        const rgba = UTIF.default.toRGBA8(ifds[0]);
        const canvas = document.createElement('canvas');
        canvas.width = ifds[0].width;
        canvas.height = ifds[0].height;
        const ctx = canvas.getContext('2d');
        const imageData = ctx.createImageData(ifds[0].width, ifds[0].height);
        imageData.data.set(rgba);
        ctx.putImageData(imageData, 0, 0);
        canvas.toBlob((canvasBlob) => {
          const url = URL.createObjectURL(canvasBlob);
          setImagePreviewUrl(url);
          setLoadingPreview(false);
        });
      } else {
        const url = URL.createObjectURL(blob);
        setImagePreviewUrl(url);
        setLoadingPreview(false);
      }
    } catch (error) {
      console.error('[handleViewImage] Error loading image preview:', error);
      alert(`Failed to load image: ${error.message}`);
      setImagePreviewUrl(null);
      setLoadingPreview(false);
    }
  };

  const closeImageModal = () => {
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
    setViewingImage(null);
    setImagePreviewUrl(null);
    setLoadingPreview(false);
    setCurrentViewMode('original');
  };

  const navigateToPreviousImage = () => {
    if (!viewingImage) return;
    if (currentViewMode === 'marked') {
      const markedImages = filteredImages.filter(img => img.s3_marked_url);
      const currentIndex = markedImages.findIndex(img => img.image_id === viewingImage.image_id);
      if (currentIndex > 0) {
        handleViewImage(markedImages[currentIndex - 1], 'marked');
      }
    } else {
      const currentIndex = filteredImages.findIndex(img => img.image_id === viewingImage.image_id);
      if (currentIndex > 0) {
        handleViewImage(filteredImages[currentIndex - 1], 'original');
      }
    }
  };

  const navigateToNextImage = () => {
    if (!viewingImage) return;
    if (currentViewMode === 'marked') {
      const markedImages = filteredImages.filter(img => img.s3_marked_url);
      const currentIndex = markedImages.findIndex(img => img.image_id === viewingImage.image_id);
      if (currentIndex < markedImages.length - 1) {
        handleViewImage(markedImages[currentIndex + 1], 'marked');
      }
    } else {
      const currentIndex = filteredImages.findIndex(img => img.image_id === viewingImage.image_id);
      if (currentIndex < filteredImages.length - 1) {
        handleViewImage(filteredImages[currentIndex + 1], 'original');
      }
    }
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!viewingImage) return;
      if (e.key === 'ArrowLeft') {
        navigateToPreviousImage();
      } else if (e.key === 'ArrowRight') {
        navigateToNextImage();
      } else if (e.key === 'Escape') {
        closeImageModal();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingImage, filteredImages]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pass':
      case 'passed':
        return 'hsl(var(--success))';
      case 'fail':
      case 'failed':
        return 'hsl(var(--destructive))';
      default:
        return 'hsl(var(--warning))';
    }
  };

  const handleImageReasonChange = (imageId, reason) => {
    setImageReasons(prev => ({ ...prev, [imageId]: reason }));
  };

  const handleAuditorReview = async (status) => {
    const imageReasonsList = Object.entries(imageReasons)
      .map(([imageId, reason]) => ({ image_id: parseInt(imageId), reason: reason || null }))
      .filter(item => item.reason);

    try {
      setReviewSubmitting(true);
      await auditorAPI.reviewBatch(batchId, {
        auditor_status: status,
        rejection_reason: null,
        image_reasons: imageReasonsList
      });
      alert(`Batch ${status} successfully! Email sent to Vendor.`);
      fetchBatchDetails(); // Refresh to show updated status
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Failed to submit review: ' + (error.response?.data?.detail || error.message));
    } finally {
      setReviewSubmitting(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.06, delayChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] }
    }
  };

  const styles = `
    .page-header {
      margin-bottom: 32px;
    }

    .page-title {
      font-size: 28px;
      font-weight: 800;
      color: hsl(var(--foreground));
      margin-bottom: 4px;
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .page-subtitle {
      font-size: 14px;
      color: hsl(var(--muted-foreground));
    }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 24px;
      margin-bottom: 32px;
    }

    .batch-info-card {
      background: var(--gradient-card);
      border-radius: var(--radius-lg);
      border: 2px solid hsl(var(--border));
      box-shadow: var(--shadow-card);
      padding: 24px;
      margin-bottom: 32px;
    }

    .batch-info-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      padding-bottom: 16px;
      border-bottom: 2px solid hsl(var(--border));
    }

    .batch-info-title {
      font-size: 18px;
      font-weight: 700;
      color: hsl(var(--foreground));
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .batch-info-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 20px;
    }

    .info-item {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .info-label {
      font-size: 12px;
      font-weight: 600;
      color: hsl(var(--muted-foreground));
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .info-value {
      font-size: 15px;
      font-weight: 600;
      color: hsl(var(--foreground));
    }

    .action-bar {
      margin-bottom: 24px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;
      gap: 16px;
    }

    .selection-info {
      font-size: 14px;
      color: hsl(var(--muted-foreground));
    }

    .selected-count {
      font-weight: 600;
      color: hsl(var(--primary));
    }

    .action-buttons {
      display: flex;
      gap: 12px;
      flex-wrap: wrap;
    }

    .content-card {
      background: var(--gradient-card);
      border-radius: var(--radius-lg);
      border: 1px solid hsl(var(--border));
      box-shadow: var(--shadow-card);
      overflow: hidden;
      margin-bottom: 24px;
    }

    .card-header {
      padding: 20px 24px;
      border-bottom: 1px solid hsl(var(--border));
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;
      gap: 16px;
    }

    .card-title {
      font-size: 18px;
      font-weight: 700;
      color: hsl(var(--foreground));
    }

    .filter-control {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .filter-label {
      font-size: 14px;
      font-weight: 500;
      color: hsl(var(--muted-foreground));
    }

    .filter-select {
      padding: 8px 16px;
      border: 2px solid hsl(var(--border));
      border-radius: var(--radius-md);
      font-size: 14px;
      font-weight: 500;
      color: hsl(var(--foreground));
      background: hsl(var(--background));
      cursor: pointer;
      outline: none;
      transition: all 0.2s;
    }

    .filter-select:hover {
      border-color: hsl(var(--primary));
    }

    .data-table {
      width: 100%;
      border-collapse: collapse;
    }

    .data-table thead {
      background: hsl(var(--secondary) / 0.5);
    }

    .data-table th {
      padding: 10px 12px;
      text-align: left;
      font-size: 11px;
      font-weight: 700;
      color: hsl(var(--muted-foreground));
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .data-table tbody tr {
      border-bottom: 1px solid hsl(var(--border));
      transition: background 0.2s;
    }

    .data-table tbody tr:hover {
      background: hsl(var(--secondary) / 0.3);
    }

    .data-table tbody tr.selected {
      background: hsl(var(--primary) / 0.1);
    }

    .data-table td {
      padding: 10px 12px;
      font-size: 13px;
      color: hsl(var(--foreground));
    }

    .checkbox {
      width: 18px;
      height: 18px;
      cursor: pointer;
    }

    .sequence-number {
      font-weight: 700;
      color: hsl(var(--primary));
      font-size: 15px;
    }

    .filename {
      font-family: 'JetBrains Mono', monospace;
      font-size: 13px;
    }

    .status-active {
      padding: 4px 12px;
      border-radius: var(--radius-full);
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      background: hsl(var(--success) / 0.15);
      color: hsl(var(--success));
    }

    .status-inactive {
      padding: 4px 12px;
      border-radius: var(--radius-full);
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      background: hsl(var(--destructive) / 0.15);
      color: hsl(var(--destructive));
    }

    .no-issues {
      color: hsl(var(--success));
      font-weight: 600;
    }

    .failed-issue {
      color: hsl(var(--destructive));
      font-weight: 600;
    }

    .failed-issues-list {
      margin: 0;
      padding-left: 20px;
    }

    .btn-view, .btn-marked {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 6px 10px;
      border: none;
      border-radius: var(--radius-md);
      font-size: 12px;
      font-weight: 600;
      white-space: nowrap;
      cursor: pointer;
      transition: all 0.2s;
    }

    .btn-view {
      background: var(--gradient-primary);
      color: white;
      box-shadow: var(--shadow-button);
    }

    .btn-view:hover {
      transform: translateY(-1px);
      box-shadow: var(--shadow-button-hover);
    }

    .btn-marked {
      background: linear-gradient(135deg, hsl(var(--warning)) 0%, hsl(var(--warning-dark, 38 92% 40%)) 100%);
      color: white;
      box-shadow: 0 4px 12px hsl(var(--warning) / 0.4);
      font-weight: 700;
      border: 2px solid hsl(var(--warning));
    }

    .btn-marked:hover {
      transform: translateY(-2px) scale(1.02);
      box-shadow: 0 8px 24px hsl(var(--warning) / 0.5);
      border-color: hsl(var(--warning-dark, 38 92% 40%));
    }

    .btn-rework {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px 24px;
      background: linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary-dark, 221 83% 45%)) 100%);
      color: white;
      border: 2px solid hsl(var(--primary));
      border-radius: var(--radius-md);
      font-size: 14px;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
      box-shadow: 0 4px 14px hsl(var(--primary) / 0.4);
      position: relative;
      overflow: hidden;
    }

    .btn-rework::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
      transition: left 0.5s;
    }

    .btn-rework:hover {
      transform: translateY(-2px) scale(1.03);
      box-shadow: 0 8px 24px hsl(var(--primary) / 0.5);
      border-color: hsl(var(--primary-dark, 221 83% 45%));
    }

    .btn-rework:hover::before {
      left: 100%;
    }

    .btn-rework:active {
      transform: translateY(0) scale(0.98);
    }

    .warning-box {
      padding: 24px;
      background: hsl(var(--warning) / 0.1);
      border: 1px solid hsl(var(--warning) / 0.3);
      border-radius: var(--radius-lg);
      display: flex;
      gap: 16px;
      align-items: flex-start;
      margin-bottom: 24px;
    }

    .warning-icon {
      font-size: 48px;
    }

    .warning-title {
      font-size: 18px;
      font-weight: 700;
      color: hsl(var(--warning));
      margin-bottom: 8px;
    }

    .warning-text {
      font-size: 14px;
      color: hsl(var(--muted-foreground));
    }

    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.85);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      padding: 20px;
      backdrop-filter: blur(8px);
    }

    .modal-content {
      background: hsl(var(--background));
      border-radius: var(--radius-lg);
      max-width: 90vw;
      max-height: 90vh;
      width: 1000px;
      display: flex;
      flex-direction: column;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
      overflow: hidden;
    }

    .modal-header {
      padding: 24px;
      border-bottom: 2px solid hsl(var(--border));
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: hsl(var(--secondary) / 0.3);
    }

    .modal-title {
      font-size: 20px;
      font-weight: 700;
      color: hsl(var(--foreground));
      margin: 0;
    }

    .modal-subtitle {
      font-size: 13px;
      color: hsl(var(--muted-foreground));
      margin: 4px 0 0 0;
    }

    .btn-close {
      width: 40px;
      height: 40px;
      border-radius: var(--radius-full);
      border: none;
      background: hsl(var(--secondary));
      color: hsl(var(--foreground));
      font-size: 20px;
      font-weight: 700;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s;
    }

    .btn-close:hover {
      transform: rotate(90deg) scale(1.1);
      background: hsl(var(--destructive) / 0.15);
      color: hsl(var(--destructive));
    }

    .image-container {
      flex: 1;
      padding: 20px;
      background: hsl(var(--secondary) / 0.2);
      overflow: auto;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      min-height: 0;
    }

    .navigation-arrow {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      width: 50px;
      height: 50px;
      background: hsl(var(--primary) / 0.8);
      color: white;
      border: none;
      border-radius: var(--radius-full);
      font-size: 24px;
      font-weight: 700;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s;
      box-shadow: var(--shadow-button);
      z-index: 10;
    }

    .navigation-arrow:hover:not(:disabled) {
      background: hsl(var(--primary));
      transform: translateY(-50%) scale(1.1);
      box-shadow: var(--shadow-button-hover);
    }

    .navigation-arrow:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    .left-arrow {
      left: 30px;
    }

    .right-arrow {
      right: 30px;
    }

    .modal-image {
      width: auto;
      height: auto;
      max-width: 100%;
      max-height: 100%;
      display: block;
      margin: auto;
      border-radius: var(--radius-md);
      box-shadow: var(--shadow-card);
    }

    .loading-spinner-container {
      text-align: center;
      padding: 60px 20px;
    }

    .modal-footer {
      padding: 20px 24px;
      border-top: 2px solid hsl(var(--border));
      display: flex;
      justify-content: center;
      align-items: center;
      background: hsl(var(--secondary) / 0.3);
    }

    .loading-container {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background: hsl(var(--background));
    }

    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    .auditor-review-card {
      background: var(--gradient-card);
      border-radius: var(--radius-lg);
      border: 2px solid hsl(var(--primary) / 0.3);
      box-shadow: var(--shadow-card);
      overflow: hidden;
      margin-bottom: 24px;
    }

    .auditor-review-header {
      padding: 20px 24px;
      border-bottom: 1px solid hsl(var(--border));
      background: hsl(var(--primary) / 0.05);
    }

    .auditor-review-title {
      font-size: 18px;
      font-weight: 700;
      color: hsl(var(--foreground));
      margin: 0 0 4px 0;
    }

    .auditor-review-subtitle {
      font-size: 13px;
      color: hsl(var(--muted-foreground));
      margin: 0;
    }

    .auditor-reason-input {
      width: 100%;
      padding: 7px 10px;
      font-size: 13px;
      border: 1px solid hsl(var(--border));
      border-radius: var(--radius-md);
      outline: none;
      font-family: var(--font-primary);
      background: hsl(var(--background));
      color: hsl(var(--foreground));
      box-sizing: border-box;
      transition: border-color 0.2s;
    }

    .auditor-reason-input:focus {
      border-color: hsl(var(--ring));
      box-shadow: 0 0 0 3px hsl(var(--ring) / 0.1);
    }

    .overall-reason-textarea {
      width: 100%;
      padding: 10px 14px;
      font-size: 14px;
      border: 2px solid hsl(var(--border));
      border-radius: var(--radius-md);
      outline: none;
      font-family: var(--font-primary);
      background: hsl(var(--background));
      color: hsl(var(--foreground));
      resize: vertical;
      transition: border-color 0.2s;
      box-sizing: border-box;
    }

    .overall-reason-textarea:focus {
      border-color: hsl(var(--destructive));
      box-shadow: 0 0 0 3px hsl(var(--destructive) / 0.1);
    }

    .review-action-buttons {
      display: flex;
      gap: 16px;
      margin-top: 20px;
    }

    .btn-approve-final {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 20px;
      background: linear-gradient(135deg, hsl(var(--success)) 0%, hsl(142 76% 30%) 100%);
      color: white;
      border: none;
      border-radius: var(--radius-md);
      font-size: 14px;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.2s;
      box-shadow: 0 4px 12px hsl(var(--success) / 0.4);
    }

    .btn-approve-final:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px hsl(var(--success) / 0.5);
    }

    .btn-approve-final:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .btn-reject-final {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 20px;
      background: linear-gradient(135deg, hsl(var(--destructive)) 0%, hsl(0 72% 35%) 100%);
      color: white;
      border: none;
      border-radius: var(--radius-md);
      font-size: 14px;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.2s;
      box-shadow: 0 4px 12px hsl(var(--destructive) / 0.4);
    }

    .btn-reject-final:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px hsl(var(--destructive) / 0.5);
    }

    .btn-reject-final:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  `;

  if (loading) {
    return (
      <>
        <style>{styles}</style>
        <div className="loading-container">
          <div style={{ textAlign: 'center' }}>
            <div className="loading-spinner" style={{ margin: '0 auto 16px' }}></div>
            <div style={{ fontSize: '14px', color: 'hsl(var(--muted-foreground))' }}>
              Loading batch details...
            </div>
          </div>
        </div>
      </>
    );
  }

  const failedImages = images.filter(img =>
    img.overall_status?.toLowerCase() === 'fail' ||
    img.validation_status?.toLowerCase() === 'fail'
  );

  return (
    <>
      <style>{styles}</style>
      <DashboardLayout user={user} logout={logout}>
        <div className="page-header">
          <h1 className="page-title">
            <Package size={28} />
            Batch Details
          </h1>
          <p className="page-subtitle">{batch?.batch_name}</p>
        </div>

        {/* Batch Information Card */}
        <motion.div
          className="batch-info-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="batch-info-header">
            <h2 className="batch-info-title">
              <Package size={20} />
              Batch Information
            </h2>
          </div>
          <div className="batch-info-grid">
            <div className="info-item">
              <span className="info-label">Batch Name</span>
              <span className="info-value">{batch?.batch_name}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Batch ID</span>
              <span className="info-value">#{batch?.batch_id}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Uploaded At</span>
              <span className="info-value">{batch?.uploaded_at ? new Date(batch.uploaded_at).toLocaleString() : 'N/A'}</span>
            </div>
            {batch?.tenant_name && (
              <div className="info-item">
                <span className="info-label">Tenant</span>
                <span className="info-value">{batch.tenant_name}</span>
              </div>
            )}
            <div className="info-item">
              <span className="info-label">Auditor Status</span>
              <span className="info-value" style={{
                color: batch?.auditor_status === 'approved' ? 'hsl(var(--success))' :
                       batch?.auditor_status === 'rejected' ? 'hsl(var(--destructive))' :
                       'hsl(var(--warning))'
              }}>
                {batch?.auditor_status ? batch.auditor_status.toUpperCase() : 'PENDING'}
              </span>
            </div>
            {batch?.rejection_reason && (
              <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                <span className="info-label">Rejection Reason</span>
                <span className="info-value" style={{ color: 'hsl(var(--destructive))', fontStyle: 'italic' }}>
                  {batch.rejection_reason}
                </span>
              </div>
            )}
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          className="stats-grid"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants} className="stat-card">
            <h3 style={{ fontSize: '22px', fontWeight: '800', color: getStatusColor(batch?.status), margin: '0 0 4px' }}>
              {batch?.status?.toUpperCase()}
            </h3>
            <p style={{ fontSize: '13px', fontWeight: '600', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Batch Status
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className="stat-card">
            <h3 style={{ fontSize: '28px', fontWeight: '800', color: 'hsl(var(--foreground))', margin: '0 0 4px' }}>
              {batch?.total_images || 0}
            </h3>
            <p style={{ fontSize: '13px', fontWeight: '600', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Total Images
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className="stat-card">
            <h3 style={{ fontSize: '28px', fontWeight: '800', color: 'hsl(var(--success))', margin: '0 0 4px' }}>
              {batch?.passed_images || 0}
            </h3>
            <p style={{ fontSize: '13px', fontWeight: '600', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Passed
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className="stat-card">
            <h3 style={{ fontSize: '28px', fontWeight: '800', color: 'hsl(var(--destructive))', margin: '0 0 4px' }}>
              {batch?.failed_images || 0}
            </h3>
            <p style={{ fontSize: '13px', fontWeight: '600', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Failed
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className="stat-card">
            <h3 style={{ fontSize: '28px', fontWeight: '800', color: 'hsl(var(--primary))', margin: '0 0 4px' }}>
              {batch?.total_images > 0
                ? Math.round((batch.passed_images / batch.total_images) * 100)
                : 0}%
            </h3>
            <p style={{ fontSize: '13px', fontWeight: '600', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Success Rate
            </p>
          </motion.div>
        </motion.div>

        {/* Batch Folder Structure */}
        <BatchFolderView batchId={batchId} userRole={user?.role} />


        {/* Action Buttons */}
        <div className="action-bar">
          <div className="selection-info">
            {selectedImages.size > 0 && (
              <span className="selected-count">
                {selectedImages.size} image(s) selected
              </span>
            )}
          </div>
          <div className="action-buttons">
            <button
              onClick={handleDownloadSelected}
              disabled={selectedImages.size === 0 || downloading}
              className="btn-primary"
              style={{ opacity: selectedImages.size === 0 ? 0.5 : 1 }}
            >
              <Download size={16} />
              {downloading ? 'Downloading...' : `Download Selected (${selectedImages.size})`}
            </button>
            {failedImages.length > 0 && (
              <button
                onClick={() => navigate(`/batch/${batchId}/rework`)}
                className="btn-rework"
              >
                <Upload size={18} />
                Upload Rework Images
              </button>
            )}
            {user?.role === 'auditor' && !batch?.auditor_status && (
              <>
                <button
                  onClick={() => handleAuditorReview('rejected')}
                  disabled={reviewSubmitting}
                  className="btn-reject-final"
                >
                  <XCircle size={16} />
                  {reviewSubmitting ? 'Submitting...' : 'Reject'}
                </button>
                <button
                  onClick={() => handleAuditorReview('approved')}
                  disabled={reviewSubmitting}
                  className="btn-approve-final"
                >
                  <CheckCircle size={16} />
                  {reviewSubmitting ? 'Submitting...' : 'Approve'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Images Table */}
        <div className="content-card">
          <div className="card-header">
            <h2 className="card-title">Image Details</h2>
            {images.length > 0 && (
              <div className="filter-control">
                <label className="filter-label">Filter by Status:</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Images ({images.length})</option>
                  <option value="pass">Pass Only ({images.filter(img => img.overall_status?.toUpperCase() === 'PASS').length})</option>
                  <option value="fail">Fail Only ({images.filter(img => img.overall_status?.toUpperCase() === 'FAIL').length})</option>
                </select>
              </div>
            )}
          </div>

          <table className="data-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedImages.size === images.length && images.length > 0}
                    onChange={handleSelectAll}
                    className="checkbox"
                  />
                </th>
                <th style={{ width: '60px' }}>Seq No</th>
                <th>Filename</th>
                <th style={{ width: '90px' }}>Status</th>
                <th>Failed Checks</th>
                <th style={{ width: '90px' }}>File Size</th>
                <th style={{ width: '120px' }}>Uploaded At</th>
                {(user?.role === 'auditor' || batch?.auditor_status) && <th>Auditor Reason</th>}
                <th style={{ width: '120px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredImages.map((image, index) => (
                <tr
                  key={image.image_id}
                  className={selectedImages.has(image.image_id) ? 'selected' : ''}
                >
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedImages.has(image.image_id)}
                      onChange={() => handleSelectImage(image.image_id)}
                      className="checkbox"
                    />
                  </td>
                  <td>
                    <span className="sequence-number">{index + 1}</span>
                  </td>
                  <td>
                    <span className="filename">{image.filename}</span>
                  </td>
                  <td>
                    <span className={image.overall_status?.toUpperCase() === 'PASS' ? 'status-active' : 'status-inactive'}>
                      {image.overall_status || 'PENDING'}
                    </span>
                  </td>
                  <td>
                    {(() => {
                      const checks = parseFailedChecks(image.failed_checks);
                      if (checks.length === 0) {
                        return <span className="no-issues">✓ No Issues</span>;
                      }
                      return (
                        <ul className="failed-issues-list">
                          {checks.map((check, idx) => (
                            <li key={idx} className="failed-issue">
                              {check.replace(/_/g, ' ')}
                            </li>
                          ))}
                        </ul>
                      );
                    })()}
                  </td>
                  <td>
                    {(image.file_size / 1024 / 1024).toFixed(2)} MB
                  </td>
                  <td>
                    {new Date(image.uploaded_at).toLocaleString()}
                  </td>
                  {(user?.role === 'auditor' || batch?.auditor_status) && (
                    <td>
                      {user?.role === 'auditor' && !batch?.auditor_status ? (
                        <input
                          type="text"
                          className="auditor-reason-input"
                          placeholder="Reason (optional)..."
                          value={imageReasons[image.image_id] || ''}
                          onChange={(e) => handleImageReasonChange(image.image_id, e.target.value)}
                        />
                      ) : (
                        <span style={{ fontSize: '13px', fontStyle: 'italic', color: image.auditor_reason ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground))' }}>
                          {image.auditor_reason || '—'}
                        </span>
                      )}
                    </td>
                  )}
                  <td>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'nowrap' }}>
                      <button
                        onClick={() => handleViewImage(image, 'original')}
                        className="btn-view"
                      >
                        <Eye size={16} />
                        View
                      </button>
                      {image.s3_marked_url && (
                        <button
                          onClick={() => handleViewImage(image, 'marked')}
                          className="btn-marked"
                        >
                          <Search size={16} />
                          View Marked
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Image Viewer Modal */}
        {viewingImage && (
          <div className="modal-overlay" onClick={closeImageModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <h3 className="modal-title">{viewingImage.filename}</h3>
                  <p className="modal-subtitle">
                    Sequence: {viewingImage.sequence_number} |
                    Size: {(viewingImage.file_size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <button onClick={closeImageModal} className="btn-close">✕</button>
              </div>
              <div className="image-container">
                {/* Navigation Arrows */}
                {(() => {
                  const navList = currentViewMode === 'marked'
                    ? filteredImages.filter(img => img.s3_marked_url)
                    : filteredImages;
                  const currentIndex = navList.findIndex(img => img.image_id === viewingImage.image_id);
                  return (
                    <>
                      <button
                        onClick={navigateToPreviousImage}
                        disabled={currentIndex <= 0}
                        className="navigation-arrow left-arrow"
                      >
                        ◀
                      </button>
                      <button
                        onClick={navigateToNextImage}
                        disabled={currentIndex === navList.length - 1}
                        className="navigation-arrow right-arrow"
                      >
                        ▶
                      </button>
                    </>
                  );
                })()}

                {/* Image Display */}
                {loadingPreview ? (
                  <div className="loading-spinner-container">
                    <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
                    <div style={{ fontSize: '16px', color: 'hsl(var(--muted-foreground))', marginTop: '16px' }}>
                      Loading {currentViewMode === 'marked' ? 'marked ' : ''}image...
                    </div>
                  </div>
                ) : imagePreviewUrl ? (
                  <img src={imagePreviewUrl} alt={viewingImage.filename} className="modal-image" />
                ) : (
                  <div className="loading-spinner">
                    <div style={{ fontSize: '64px', opacity: 0.5 }}>🖼️</div>
                    <div style={{ fontSize: '16px', color: 'hsl(var(--muted-foreground))', marginTop: '16px' }}>
                      Image not available
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  onClick={async () => {
                    setDownloadingSingle(true);
                    try {
                      await batchAPI.downloadSingleImage(batchId, viewingImage.image_id, viewingImage.filename);
                    } finally {
                      setDownloadingSingle(false);
                    }
                  }}
                  className="btn-primary"
                  disabled={downloadingSingle}
                >
                  <Download size={16} />
                  {downloadingSingle ? 'Downloading...' : 'Download Image'}
                </button>
              </div>
            </div>
          </div>
        )}
      </DashboardLayout>
    </>
  );
};

export default BatchDetails;
